/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import crypto from "crypto-js";
import React, { useEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { checkPathnameIncludes } from "../_metronic/_helpers/Pages";
import { ContentRoute, Layout, LayoutSplashScreen } from "../_metronic/layout";
import BasePage from "./BasePage";
import SupportAlert from "./components/SupportAlert";
import { Logout } from "./modules/Auth";
import * as auth from "./modules/Auth/_redux/authRedux";
import AuthCommunity from "./modules/Auth/pages/AuthCommunity/index";
import { AuthPage } from "./modules/Auth/pages/AuthPage";
import Callback from "./modules/Auth/pages/Callback";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import PasswordReset from "./modules/PasswordReset";
import { PaymentLink } from "./modules/PaymentLink";
import { PaymentLinkV2 } from "./modules/PaymentLink/v2";
import ForgotPassword from "./modules/ForgotPassword";
import Simulation from "./modules/Simulation";
import Validation from "./modules/Validation";
import FirstAccess from "./pages/FirstAccess";
import { FIRST_ACCESS_ROUTES } from "./pages/FirstAccess/routes";
import { ROUTES_PATH } from "./constants/Routes";
import {
  CadastroV2Route,
  RegisterCepRedirect,
  RegisterRoute,
} from "./routes/RegisterRoutes";

import CompleteProfile from "./pages/CompleteProfile";
import ProfileSurvey from "./pages/ProfileSurvey";
import JusZapPage from "./pages/JusZapPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import JusfyPrivacyPolicy from "./pages/JusfyPrivacyPolicy";
import RelationQuery from "./pages/RelationQuery";
import { SocketProvider } from "./modules/SocketProvider";
import { RealtimeNotificationsProvider } from "./modules/RealtimeNotificationsProvider";
import RealtimeToastPoc from "./components/RealtimeToastPoc";
import { useRouteNoindex } from "./hooks/useRouteNoindex";
import { useUserPreferences } from "./services/userPreferences";
import { POST_LOGIN_REDIRECT_KEY } from "./constants/postLoginRedirect";

const OnboardingAddress = React.lazy(() =>
  import("./pages/OnboardingAddress")
);
const OnboardingOab = React.lazy(() => import("./pages/OnboardingOab"));
const OnboardingWelcome = React.lazy(() =>
  import("./pages/OnboardingWelcome")
);
const OnboardingOffice = React.lazy(() => import("./pages/OnboardingOffice"));
const OnboardingPhoto = React.lazy(() => import("./pages/OnboardingPhoto"));
const OnboardingTools = React.lazy(() =>
  import("./pages/OnboardingTools")
);

// Página pública de documento legal (issue jusfy-admin#247): a política de
// privacidade, termos de uso etc. deixam de ser hardcoded e passam a ser
// servidos pelo backend em /documentos/:slug. Lazy: chunk separado.
const LegalDocumentPage = React.lazy(() => import("./pages/LegalDocument"));

const POST_LOGIN_REDIRECT_BLOCKLIST = [ROUTES_PATH.AUTH.LOGOUT, "/error", "/auth"];

function sanitizeInternalPath(path) {
  if (typeof path !== "string") return null;
  if (!path.startsWith("/")) return null;
  if (path.startsWith("//")) return null;
  if (path.includes("://")) return null;
  const pathnameOnly = path.split(/[?#]/)[0];
  const isBlocked = POST_LOGIN_REDIRECT_BLOCKLIST.some(
    blocked => pathnameOnly === blocked || pathnameOnly.startsWith(`${blocked}/`)
  );
  if (isBlocked) return null;
  return path;
}

export function Routes() {
  useRouteNoindex();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search;

  const isImpersonate = pathname === "/auth/impersonate";

  const { isAuthorized, authToken } = useSelector(
    ({ auth }) => ({
      isAuthorized: auth.user != null,
      authToken: auth.authToken,
    }),
    shallowEqual
  );

  const isCommunity = search.includes("sso") && search.includes("sig");

  useEffect(() => {
    if (isCommunity) {
      dispatch({
        type: auth.actionTypes.SetOrigin,
        payload: "community",
      });
    }
  }, [dispatch, isCommunity]);

  const subscriptionStatus = useSelector(state => state.auth?.user);
  const haveClient = subscriptionStatus?.role === "external_user";

  useEffect(() => {
    const ROUTES_WITH_BUILD_VALIDATION = [
      "/dashboard",
      "/jusfinder",
      "/peticoes",
      "/oportunidades",
      "/agenda",
      "/assinaturas/form",
    ];

    const validateBuildHash = async () => {
      try {
        const manifestResponse = await fetch("/asset-manifest.json", {
          cache: "no-store",
        });
        if (!manifestResponse.ok) {
          return;
        }
        const manifest = await manifestResponse.text();

        const hashHex = crypto.SHA256(manifest).toString(crypto.enc.Hex);

        const cachedHash = localStorage.getItem("buildHash");

        if (cachedHash !== hashHex) {
          dispatch({
            type: auth.actionTypes.UserRequested,
            payload: {
              callback: () => {
                localStorage.setItem("buildHash", hashHex);
              },
            },
          });
        }
      } catch (error) {
        console.error("Falha ao validar o hash do build:", error);
      }
    };

    if (isAuthorized && checkPathnameIncludes(ROUTES_WITH_BUILD_VALIDATION)) {
      validateBuildHash();
    }
  }, [dispatch, isAuthorized, pathname]);

  const storeTargetAndRedirectToLogin = () => {
    const target = sanitizeInternalPath(pathname + search);
    if (target) {
      try {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, target);
      } catch {
        // storage bloqueado: segue sem lembrar o destino pós-login
      }
    }
    return <Redirect to="/auth/login" />;
  };

  return (
    <Switch>
      <Route path="/forgot_password" component={ForgotPassword} />
      <Route path="/password_reset/:token" component={PasswordReset} />
      <Route path="/validation/:token" component={Validation} />
      <Route path="/simulation" component={Simulation} />
      <Route path="/privacidade-ia-whatsapp" component={PrivacyPolicy} />
      <Route path="/politica-de-privacidade" component={JusfyPrivacyPolicy} />
      <Route path="/ia-assistente-whatsapp" component={JusZapPage} />
      <Route path={FIRST_ACCESS_ROUTES.BASE} component={FirstAccess} />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.WELCOME}
        render={() => {
          if (isAuthorized) return <OnboardingWelcome />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.ADDRESS}
        render={() => {
          if (isAuthorized) return <OnboardingAddress />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.OAB}
        render={() => {
          if (isAuthorized) return <OnboardingOab />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.OFFICE}
        render={() => {
          if (isAuthorized) return <OnboardingOffice />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.PHOTO}
        render={() => {
          if (isAuthorized) return <OnboardingPhoto />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />
      <Route
        exact
        path={ROUTES_PATH.ONBOARDING.TOOLS}
        render={() => {
          if (isAuthorized) return <OnboardingTools />;
          return authToken ? <LayoutSplashScreen /> : storeTargetAndRedirectToLogin();
        }}
      />

      <Route
        path="/register/:redirect?"
        render={props => <RegisterRoute {...props} />}
      />
      <Route
        path="/cadastro/:redirect?"
        render={props => <CadastroV2Route {...props} />}
      />
      <Route
        path="/register-cep/:redirect?"
        render={props => <RegisterCepRedirect {...props} />}
      />



      <Route path="/complete_profile" component={SupportAlert} />
      <Route path="/chore/complete_profile" component={CompleteProfile} />
      {/* JusPDF descontinuado (set/2026): rota mantida só para redirecionar quem chega por URL direta. */}
      <Redirect from="/pdf" to="/" />
      <Route
        path="/relation_query/:document_type/:id"
        component={RelationQuery}
      />

      <Route path="/profile/survey" component={ProfileSurvey} />

      {isCommunity ? (
        <Route path="/community" component={AuthCommunity} />
      ) : null}

      <ContentRoute path="/payment-link/v2/:link" component={PaymentLinkV2} />
      <ContentRoute path="/payment-link/:link" component={PaymentLink} />

      <Route path="/auth/impersonate" component={AuthPage} />

      <Route
        path="/auth"
        render={() => {
          if (isImpersonate || !isAuthorized) {
            return <AuthPage />;
          }

          let stored = null;
          try {
            stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
            if (stored) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
          } catch {
            // storage bloqueado: sem destino salvo, cai em "/"
          }
          if (stored) {
            const target = sanitizeInternalPath(stored);
            if (target) {
              return <Redirect to={target} />;
            }
          }

          return <Redirect to="/" />;
        }}
      />

      <Route path="/error" component={ErrorsPage} />
      <Route path={ROUTES_PATH.AUTH.LOGOUT} component={Logout} />
      {isAuthorized ? <Redirect exact from="/callback" to="/auth" /> : null}
      <Route exact path="/callback" component={Callback} />

      {/* Documentos legais dinâmicos (issue jusfy-admin#247): prefixo fixo
          /documentos/:slug — nunca colide com rota de raiz do app, presente ou
          futura. Servidos pelo backend, sem auth e sem deploy. A política
          legada hardcoded segue em /politica-de-privacidade (rota acima);
          a versão gerada no admin vive em /documentos/politica-de-privacidade. */}
      <Route exact path="/documentos/:slug" component={LegalDocumentPage} />

      {haveClient ? (
        <Redirect to="/chore/complete_profile?compra_garantida=true" />
      ) : null}
      {isAuthorized ? (
        <SocketProvider>
          <RealtimeNotificationsProvider>
            <RealtimeToastPoc />
            <Layout>
              <BasePage />
            </Layout>
          </RealtimeNotificationsProvider>
        </SocketProvider>
      ) : authToken ? (
        <LayoutSplashScreen />
      ) : (
        storeTargetAndRedirectToLogin()
      )}
    </Switch>
  );
}
